// SMS length + segmentation, aligned with Twilio's per-message restriction.
// Twilio accepts up to 1600 chars/message (auto-segmented; the phone
// reassembles), and bills per segment. We cap user input below 1600 to reserve
// headroom for any appended footer (opt-out/compliance). Encoding decides the
// per-segment size: GSM-7 = 160 (153 when concatenated); Unicode = 70 (67).
// Keep these values in sync with maxanalytics-server (messaging.config.js).
export const SMS_TWILIO_MAX = 1600;
export const SMS_FILLER_RESERVE = 60;
export const SMS_BODY_MAX = SMS_TWILIO_MAX - SMS_FILLER_RESERVE;

const GSM7_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXTENDED = '^{}\\[~]|€';

const isGsm7 = (text: string): boolean =>
  Array.from(text).every((c) => GSM7_BASIC.includes(c) || GSM7_EXTENDED.includes(c));

// GSM-7 septet count — extended-table chars (^ { } [ ] ~ \ | €) take two.
const gsm7Units = (text: string): number =>
  Array.from(text).reduce((n, c) => n + (GSM7_EXTENDED.includes(c) ? 2 : 1), 0);

export interface ISmsSegmentInfo {
  encoding: 'GSM-7' | 'Unicode';
  // UTF-16 length — matches what the textarea maxLength and Yup .max enforce.
  length: number;
  segments: number;
  // Chars that fit in a single segment for this encoding (160 GSM-7 / 70 Unicode).
  singleSegmentMax: number;
  remaining: number;
  overLimit: boolean;
}

export const getSmsSegmentInfo = (text: string): ISmsSegmentInfo => {
  const value = text || '';
  const gsm = isGsm7(value);
  const singleSegmentMax = gsm ? 160 : 70;
  const concatSegment = gsm ? 153 : 67;
  // Segment math runs on encoded units (GSM septets / UTF-16 code units); the
  // displayed/enforced length is value.length so it matches the input cap.
  const units = gsm ? gsm7Units(value) : value.length;

  let segments;
  if (units === 0) segments = 0;
  else if (units <= singleSegmentMax) segments = 1;
  else segments = Math.ceil(units / concatSegment);

  return {
    encoding: gsm ? 'GSM-7' : 'Unicode',
    length: value.length,
    segments,
    singleSegmentMax,
    remaining: SMS_BODY_MAX - value.length,
    overLimit: value.length > SMS_BODY_MAX
  };
};
